// Utilities to reduce "raw markdown/latex" flicker while streaming.
//
// When rendering partial streamed output, Markdown fences (```), display math ($$)
// and inline math ($) are often temporarily unbalanced. Most markdown/math
// renderers will then treat the content as plain text until the closing token
// arrives, causing a visible "snap" at the end.
//
// This helper appends temporary closing tokens *for display only*.
// It is deterministic and should be re-run on each render/update.

export const makeStreamingMarkdownSafe = (input) => {
  const text = typeof input === 'string' ? input : String(input ?? '');
  if (!text) return '';

  let out = text;

  // Close any unbalanced fenced code block.
  // Important: Markdown fences can be 3+ backticks OR 3+ tildes, and the closing fence
  // must be at least as long as the opening fence. The previous implementation only
  // handled literal "```" which fails for 4+ backticks and "~~~" fences.
  const lines = out.split('\n');
  let inFence = false;
  let fenceChar = null;
  let fenceLen = 0;

  const matchFence = (line) => {
    const m = /^(\s*)(`{3,}|~{3,})(.*)$/.exec(line);
    if (!m) return null;
    const fence = m[2];
    return { fence, fenceChar: fence[0], fenceLen: fence.length };
  };

  for (const line of lines) {
    const info = matchFence(line);
    if (!info) continue;

    if (!inFence) {
      // Opening fence.
      inFence = true;
      fenceChar = info.fenceChar;
      fenceLen = info.fenceLen;
      continue;
    }

    // Closing fence: same char and length >= opening fence.
    if (info.fenceChar === fenceChar && info.fenceLen >= fenceLen) {
      inFence = false;
      fenceChar = null;
      fenceLen = 0;
    }
  }

  if (inFence && fenceChar && fenceLen >= 3) {
    out += `\n${fenceChar.repeat(fenceLen)}`;
  }

  // Scan for unescaped $ tokens outside code fences, distinguishing $$ from $.
  let doubleDollarCount = 0;
  let singleDollarCount = 0;
  inFence = false;
  fenceChar = null;
  fenceLen = 0;

  for (const line of out.split('\n')) {
    const info = matchFence(line);
    if (info) {
      if (!inFence) {
        inFence = true;
        fenceChar = info.fenceChar;
        fenceLen = info.fenceLen;
        continue;
      }
      if (info.fenceChar === fenceChar && info.fenceLen >= fenceLen) {
        inFence = false;
        fenceChar = null;
        fenceLen = 0;
        continue;
      }
    }

    if (inFence) continue;

    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (ch === '\\') {
        i += 1;
        continue;
      }
      if (ch !== '$') continue;

      const next = line[i + 1];
      if (next === '$') {
        doubleDollarCount += 1;
        i += 1;
        continue;
      }

      singleDollarCount += 1;
    }
  }

  // If there's an odd number of $$ tokens, close display math.
  if (doubleDollarCount % 2 === 1) {
    out += '\n$$';
  }

  // If there's an odd number of single $ tokens, close inline math.
  // Note: this may treat currency like math while streaming, but it significantly
  // reduces LaTeX flicker for typical chat answers.
  if (singleDollarCount % 2 === 1) {
    out += '$';
  }

  return out;
};
